/**
 * Push-Plan abgleichen (F-31): Ist dieses Geraet die Planquelle (das Geraet
 * mit den Daten) und der Dienst erreichbar, bekommt er bei jeder Aenderung
 * den Plan der naechsten Tage - nur Zeitpunkt, kurzer Text und Link, nie
 * die Datenbank. Ein eigenes Push-Abo braucht die Planquelle nicht: Der
 * Laptop liefert, das Handy empfaengt. Erledigtes faellt beim naechsten
 * Abgleich heraus und wird nicht mehr gesendet.
 */
import { useEffect, useMemo } from 'react';

import { planSenden, type PlanZeile } from '@/modules/benachrichtigungen/push/api';
import { geraeteName, usePush, ZULETZT_GESENDET } from '@/modules/benachrichtigungen/push/client';
import { pushPlan } from '@/domain/benachrichtigungen/zustand';
import { parseLocal } from '@/lib/datetime';
import type { MeldungsDaten } from '@/modules/benachrichtigungen/useMeldungen';

const WARTEN_MS = 2_000;

/** Lokale Zeit -> UTC. Die Zeitzone des Browsers (Europe/Berlin) entscheidet, auch ueber die Zeitumstellung. */
export function planZeilen(daten: Pick<MeldungsDaten, 'alle' | 'zustand' | 'jetzt' | 'einstellungen'>): PlanZeile[] {
  return pushPlan(daten.alle, daten.zustand, daten.jetzt, daten.einstellungen.neutral)
    .map((p) => ({ id: p.id, faellig: parseLocal(p.zeit).toISOString(), titel: p.titel, text: p.text, link: p.link }));
}

export function usePushAbgleich(daten: MeldungsDaten): void {
  const dienst = usePush((s) => s.dienst);
  const planQuelle = usePush((s) => s.planQuelle);
  const pruefen = usePush((s) => s.pruefen);
  useEffect(() => { void pruefen(); }, [pruefen]);
  const zeilen = useMemo(() => (daten.geladen ? planZeilen(daten) : null), [daten]);
  const json = zeilen ? JSON.stringify(zeilen) : null;

  useEffect(() => {
    if (!planQuelle || dienst !== true || !json) return;
    let alt: string | null = null;
    try { alt = localStorage.getItem(ZULETZT_GESENDET); } catch { /* egal */ }
    if (alt === json) return;
    const t = setTimeout(() => {
      void planSenden(geraeteName(), JSON.parse(json) as PlanZeile[])
        .then(() => {
          try { localStorage.setItem(ZULETZT_GESENDET, json); } catch { /* egal */ }
          void pruefen();
        })
        .catch(() => undefined);
    }, WARTEN_MS);
    return () => clearTimeout(t);
  }, [planQuelle, dienst, json, pruefen]);
}
